import { useCallback, useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { deleteGoal, fetchGoals } from "../../../store/slices/admin/goals";
import Dropdown from "../common/Dropdown";
import Modal from "../common/modal/Modal";
import ModalBody from "../common/modal/ModalBody";
import ModalFooter from "../common/modal/ModalFooter";
import ModalHeader from "../common/modal/ModalHeader";
import { formatDate } from "../helper/formatters";
import { close, threeDots } from "../icons";
import Loader from "../Loader";
import { showNotification } from "../../../store/slices/admin/notification";
import moment from "moment";

const statusTextColors: any = {
	ended: { color: "#38C089", text: "Ended" },
	upcoming: { color: "#F5BF41", text: "Upcoming" },
	running: { color: "#0086FF", text: "Running" },
};

export default function GoalsTable(props: { isSuperUser: any }) {
	const dispatch = useDispatch();
	const isLoading = useSelector((state: any) => state.goals.isLoading);
	const troubleLoading = useSelector((state: any) => state.goals.troubleLoading);
	const [isCopyModalOpen, setCopyModalOpen] = useState(false);
	const [copyModalUrl, setCopyModalUrl] = useState("");
	const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deleteId, setDeleteId] = useState();
	const toggleCopyModal = useCallback(
		(id?) => {
			setCopyModalOpen(!isCopyModalOpen);
			setCopyModalUrl(`${window.location.origin}/goals/${id}`);
		},
		[isCopyModalOpen]
	);
	const toggleDeleteModal = useCallback(
		(id?) => {
			setDeleteModalOpen(!isDeleteModalOpen);
			setDeleteId(id);
		},
		[isDeleteModalOpen]
	);
	const handleDeleteGoal = useCallback(() => {
		dispatch(deleteGoal(deleteId));
		toggleDeleteModal();
	}, [deleteId, dispatch, toggleDeleteModal]);
	const handleCopy = useCallback(() => {
		dispatch(showNotification("success", "URL copied to clipboard!"));
	}, [dispatch]);

	const [filteredStatuses, setFilteredStatuses]: any = useState({
		running: true,
		ended: true,
		upcoming: true,
	});
	const handleFilter = (e: any) => {
		const temp = { ...filteredStatuses };
		temp[e.target.name] = e.target.checked;
		setFilteredStatuses(temp);
	};
	useEffect(() => {
		dispatch(fetchGoals());
	}, [dispatch]);
	const goals = useSelector((state: any) => state.goals.data || []);

	return (
		<div className="dashboards__table">
			<div className="table-header">
				<h4>Active Goals</h4>
				<div className="form-group search">
					Filter: Running
					<input
						type="checkbox"
						name="running"
						checked={filteredStatuses.running}
						onChange={handleFilter}
					/>
					Upcoming
					<input
						type="checkbox"
						name="upcoming"
						checked={filteredStatuses.upcoming}
						onChange={handleFilter}
					/>
					Ended
					<input
						type="checkbox"
						name="ended"
						checked={filteredStatuses.ended}
						onChange={handleFilter}
					/>
				</div>
			</div>
			{ troubleLoading ? 
				<table className="table">
					<tbody>
						<tr className="table__loading-row">
						<td colSpan={999}>
							<span className="trouble-loading-message">
								<h5>We're having trouble loading your data</h5>
								<button className="btn btn-outline-primary" onClick={()=>{ window.location.reload()}}>
									Please try again
								</button>
							</span>
						</td>
						</tr>
					</tbody>
				</table> :
			<Loader isLoading={isLoading} customClass={"table-loader"}>
				<table className="table">
					<tbody>
						{goals.length ? (
							goals
								.filter(
									(item: any) => filteredStatuses[item.status]
								)
								.map((item: any) => (
									<tr key={item.id}>
										<td>
											<a
												href={`/goals/${item.id}`}
												target="_blank"
												rel="noreferrer"
												className="title-link"
											>
												{item.title}</a>
										</td>
										<td>
											{item.recurrencyType === "onetime"
												? "One-time"
												: "Recurring"}
										</td>
										<td
											style={{
												color: statusTextColors[
													item.status
												].color,
											}}
										>
											{statusTextColors[item.status].text}
										</td>
										<td>by {item.createdBy.name}</td>
										<td>
											Created{" "}
											{item.creationDate
												? formatDate(item.creationDate)
												: ""}
										</td>
										<td>
											Start{" "}
											{item.startDate
												? formatDate(item.startDate)
												: ""}
										</td>
										<td>
											{moment(item.endDate).diff(moment.utc(), "y") < 50
												? "End "+formatDate(item.endDate)
												: "Neverending"}
										</td>
										<td className="buttons">
											<Dropdown
												buttonText={threeDots}
												className="dashboards-dropdown"
											>
												{item.status !== "upcoming" && (
													<a
														href={`/goals/${item.id}`}
														target="_blank"
														rel="noreferrer"
														className="dropdown-item"
													>
														View
													</a>
												)}

												{props.isSuperUser && (
													<Link
														className="dropdown-item"
														to={`edit-goal/${item.id}`}
													>
														Edit
													</Link>
												)}
												{item.recurrencyType !==
													"onetime" && (
													<Link
														className="dropdown-item"
														to={`archive/${item.id}`}
													>
														View archive
													</Link>
												)}

												{item.status !== "upcoming" && (
													<button
														className="dropdown-item"
														onClick={() =>
															toggleCopyModal(
																item.id
															)
														}
													>
														Get link
													</button>
												)}

												{props.isSuperUser && (
													<>
														<div className="dropdown-divider" />
														<button
															className="dropdown-item delete"
															onClick={() =>
																toggleDeleteModal(
																	item.id
																)
															}
														>
															Delete
														</button>
													</>
												)}
											</Dropdown>
										</td>
									</tr>
								))
						) : (
							<tr>
								<td>No goals defined</td>
							</tr>
						)}
					</tbody>
				</table>
			</Loader>}

			<Modal isOpen={isCopyModalOpen} className="copy-url-modal">
				<ModalHeader>
					<h5 className="modal-title">Copy dashboard link</h5>
					<button
						type="button"
						className="btn btn-link"
						aria-label="Close"
						onClick={() => toggleCopyModal("")}
					>
						<span aria-hidden="true">{close}</span>
					</button>
				</ModalHeader>
				<ModalBody>
					<div className="form-group mt-2">
						<input
							className="form-control"
							type="text"
							readOnly
							value={copyModalUrl}
						/>
						<CopyToClipboard text={copyModalUrl}>
							<button
								className="btn btn-outline-primary"
								onClick={handleCopy}
							>
								Copy
							</button>
						</CopyToClipboard>
					</div>
				</ModalBody>
				<ModalFooter>
					<button
						type="button"
						className="btn btn-outline-danger"
						onClick={() => toggleCopyModal("")}
					>
						Close
					</button>
				</ModalFooter>
			</Modal>

			<Modal isOpen={isDeleteModalOpen} className="confirm">
				<ModalHeader>
					<p className="modal-title">
						Are you sure you want to delete this dashboard?
					</p>
				</ModalHeader>
				<ModalFooter>
					<button
						type="button"
						className="btn btn-outline-danger"
						onClick={() => toggleDeleteModal("")}
					>
						Close
					</button>
					<button
						type="button"
						className="btn btn-danger"
						onClick={handleDeleteGoal}
					>
						Delete
					</button>
				</ModalFooter>
			</Modal>
		</div>
	);
}
